import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { userAuthService } from '../services/userAuthService';
import LanguageSwitcher from './LanguageSwitcher';

interface UserDashboardProps {
  userData: any;
  onLogout: () => void;
}

const UserDashboard: React.FC<UserDashboardProps> = ({ userData, onLogout }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadUserProfile = async () => {
      if (userData?.mobile_number) {
        try {
          const response = await userAuthService.getUserProfile(userData.mobile_number);
          if (response.success) {
            setUserProfile(response.data);
          } else {
            setError(response.error || 'Failed to load profile');
          }
        } catch (err) {
          setError('Failed to load profile');
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [userData]);

  const handleLogout = () => {
    onLogout();
    navigate('/');
  };

  const handleBackToMain = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black/20 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-md mx-auto bg-white/10 rounded-lg p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-3"></div>
            <p className="text-white text-center">{t('auth.loadingProfile')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black/20 backdrop-blur-sm flex items-center justify-center">
      <div className="w-full max-w-4xl mx-auto px-4 py-8">
        {/* Language Switcher */}
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>
        
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <button
            onClick={handleBackToMain}
            className="text-white hover:text-orange-200 transition-colors"
          >
            ← {t('pages.userLogin.backToMain')}
          </button>
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                <span className="text-white text-lg">🙏</span>
              </div>
              <h1 className="text-lg font-bold text-white text-center">{t('dashboard.profile')}</h1>
              <p className="text-white/80 text-sm text-center">{t('dashboard.welcome')}, {userProfile?.user_name || 'User'}</p>
            </div>
            <button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition duration-200"
            >
              {t('navigation.logout')}
            </button>
          </div>
        </div>

        {error && (
          <div className="max-w-2xl mx-auto mb-8 bg-red-500/20 rounded-lg p-4 text-center">
            <p className="text-red-200 text-center">{error}</p>
          </div>
        )}

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Profile Card */}
            <div className="lg:col-span-1">
              <div className="bg-white/10 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white mb-4">Profile Information</h2>
                
                {userProfile ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-1">Full Name</label>
                      <p className="text-white font-semibold">{userProfile.user_name}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-1">Mobile Number</label>
                      <p className="text-white font-semibold">{userProfile.mobile_number}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-1">Email</label>
                      <p className="text-white font-semibold">{userProfile.email_id}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-1">Location</label>
                      <p className="text-white font-semibold">
                        {userProfile.user_city && userProfile.user_state 
                          ? `${userProfile.user_city}, ${userProfile.user_state}` 
                          : 'Not specified'}
                      </p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-1">PIN Code</label>
                      <p className="text-white font-semibold">{userProfile.user_pincode}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-1">Date of Birth</label>
                      <p className="text-white font-semibold">{userProfile.date_of_birth}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-white/80">Profile information not available</p>
                )}
              </div>
            </div>

            {/* Nominee Information */}
            <div className="lg:col-span-1">
              <div className="bg-white/10 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white mb-4">Nominee Information</h2>
                
                {userProfile ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-1">Nominee Name</label>
                      <p className="text-white font-semibold">{userProfile.nominee_name}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-1">Relation</label>
                      <p className="text-white font-semibold capitalize">{userProfile.nominee_relation}</p>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-white/80 mb-1">Nominee Date of Birth</label>
                      <p className="text-white font-semibold">{userProfile.nominee_date_of_birth}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-white/80">Nominee information not available</p>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="lg:col-span-1">
              <div className="bg-white/10 rounded-lg p-6">
                <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
                
                {/* Live Rates Notice */}
                <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mb-4">
                  <h3 className="text-green-200 font-semibold mb-2">🟢 Live Gold & Silver Rates</h3>
                  <p className="text-green-100 text-sm mb-3">
                    View real-time rates from Augmont API and make donations with current market prices.
                  </p>
                  <div className="text-green-200 text-xs">
                    <p>• Rates update every minute</p>
                    <p>• 5-minute validity period</p>
                    <p>• Direct from Augmont API</p>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <button 
                    onClick={() => navigate('/users/donation')}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-4 rounded-lg transition duration-200 flex items-center justify-center space-x-2 text-lg"
                  >
                    <span>💰</span>
                    <span>Make Donation with Live Rates</span>
                  </button>
                  
                  <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center space-x-2">
                    <span>📋</span>
                    <span>View History</span>
                  </button>
                  
                  <button className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center space-x-2">
                    <span>⚙️</span>
                    <span>Settings</span>
                  </button>
                  
                  <button className="w-full bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center space-x-2">
                    <span>📞</span>
                    <span>Contact Support</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="mt-8">
            <div className="bg-white/10 rounded-lg p-6">
              <h2 className="text-xl font-bold text-white mb-4">Recent Activity</h2>
              
              <div className="text-center py-8">
                <p className="text-white/80">No recent activity to display</p>
                <p className="text-white/60 text-sm mt-2">Your donation history and activities will appear here</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* AUGMONT Logo */}
            <div className="bg-white rounded-lg px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">Powered By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/Asset1.png" 
                    alt="AUGMONT GOLD" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
            
            {/* Mandirsetu Initiative */}
            <div className="bg-white rounded-lg px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">An Initiative By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/mandirSetu.png" 
                    alt="An Initiative By Mandirsetu" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard; 